import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  LockOutlined as LockOutlinedIcon,
  Save as SaveIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import {
  tienePermisosModulo,
  puedeEditarProgramaContabilidad,
} from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';
import { useProductosMaterialContext } from 'context';
import tiposDocumentosJson from 'utils/tiposDocumentos.json';

const MOTORCONTABLE_ENTRADA =
  tiposDocumentosJson.find((e) => Array.isArray(e?.motorcontable))?.motorcontable || [];

/** Opciones del select "Tipo de Operacion" (motorcontable), sin entradas que son nombres de campo. */
const TIPOS_OPERACION_MOTOR = MOTORCONTABLE_ENTRADA.filter((item) => {
  const t = String(item || '').trim();
  if (!t) return false;
  if (/^[a-z][a-z0-9_]*$/.test(t)) return false;
  return true;
});

const claveMotor = (tipooperacion) => String(tipooperacion || '').toLowerCase().replace(/\s/g, '');

const bloqueMotorDeTipo = (tipooperacion) => {
  const clave = claveMotor(tipooperacion);
  if (!clave) return null;
  return tiposDocumentosJson.find((e) => e?.[clave])?.[clave] || null;
};

const tipo_regla1 = (tipooperacion) => bloqueMotorDeTipo(tipooperacion)?.tipo_documento || [];

/** Reglas (tipo_documento) que toman la cuenta de formas de pago, si el bloque lo define. */
const origenFormaPagoReglas = (tipooperacion) => {
  const bloque = bloqueMotorDeTipo(tipooperacion);
  if (!bloque) return [];
  const raw = bloque.origenformapago ?? bloque.origenformadepago ?? [];
  return (Array.isArray(raw) ? raw : [raw])
    .map((x) => String(x || '').trim())
    .filter(Boolean);
};

const codigoUsaOrigenFormaPago = (tipooperacion, codigoRegla) => {
  const codigo = String(codigoRegla || '').trim().toLowerCase();
  if (!codigo) return false;
  return origenFormaPagoReglas(tipooperacion).some((c) => c.toLowerCase() === codigo);
};

const LADO_DEBITO_CREDITO = [
  { value: 'Debito', label: 'Debito' },
  { value: 'Credito', label: 'Credito' },
];

const APLICA_A_OPCIONES = [
  { value: 'cuenta', label: 'Una cuenta' },
  { value: 'json', label: 'JSON de cuentas' },
];

const etiquetaAplicaA = (valor) =>
  APLICA_A_OPCIONES.find((o) => o.value === valor)?.label || 'Una cuenta';

const etiquetaDebitoCredito = (valor) =>
  LADO_DEBITO_CREDITO.find((o) => o.value === valor)?.label || valor || '—';

// Nombre legible de los códigos de regla de la contabilización diaria: el
// código solo («cxc», «otrosingresos») se presta a asignar la cuenta a la
// regla equivocada. Los demás tipos se muestran como siempre.
const ETIQUETA_CODIGO_REGLA = {
  cxc: 'Cuentas por cobrar préstamos',
  interesdiferido: 'Intereses diferidos',
  ingresointeres: 'Ingresos por intereses',
  ingresomora: 'Ingresos por mora',
  otrosingresos: 'Otros ingresos de cobranza (incautos, llamadas)',
  apertura: 'Contrapartida de apertura (capital de la cartera)',
  caja: 'Caja / banco',
  gasto: 'Cuenta de la categoría del gasto',
  pago: 'Caja / banco con que se paga',
};

const etiquetaCodigoRegla = (codigo) => {
  const c = String(codigo || '').trim();
  const nombre = ETIQUETA_CODIGO_REGLA[c.toLowerCase()];
  return nombre ? `${nombre} (${c})` : c;
};

const etiquetaCuentaCatalogo = (codigo, filasCatalogo) => {
  const c = String(codigo || '').trim();
  if (!c) return '—';
  const row = (filasCatalogo || []).find((x) => String(x.cuenta ?? '').trim() === c);
  const nom = row?.nombre != null ? String(row.nombre).trim() : '';
  return nom ? `${c}-${nom}` : c;
};

const etiquetaCuentasRegla = (registro, filasCatalogo, tipoOperacionMotor) => {
  if (codigoUsaOrigenFormaPago(tipoOperacionMotor, registro?.codigo_regla)) {
    return 'Cuenta de la forma de pago';
  }
  const aplica = String(registro?.aplica_a || 'cuenta').trim().toLowerCase();
  if (aplica === 'json') {
    return 'Recibe JSON al procesar';
  }
  return etiquetaCuentaCatalogo(registro?.cuentacontable, filasCatalogo);
};

const etiquetaAplicaARegla = (registro, tipoOperacionMotor) => {
  if (codigoUsaOrigenFormaPago(tipoOperacionMotor, registro?.codigo_regla)) {
    return 'Forma de pago';
  }
  return etiquetaAplicaA(registro?.aplica_a);
};

const labelOpcionCatalogo = (opt) => {
  if (!opt || opt.cuenta == null) return '';
  const cu = String(opt.cuenta).trim();
  const nom = opt.nombre != null ? String(opt.nombre).trim() : '';
  return nom ? `${cu}-${nom}` : cu;
};

const valoresIniciales = {
  tipoDocumento: '',
  codigoRegla: '',
  tipoOperacion: '',
  aplicaA: 'cuenta',
  cuentaContable: '',
};

const PROGRAMA = 'configuracioncuentascontables';

const ConfiguracionDocumentoContable = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { nombreNegocio, usuarioLog, claveLog, roleDetalles } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer = tienePermisosModulo(roleDetalles, PROGRAMA);
  const puedeModificar = puedeEditarProgramaContabilidad(roleDetalles, PROGRAMA);
  const puedeEliminar = puedeEditarProgramaContabilidad(roleDetalles, PROGRAMA);

  const [formulario, setFormulario] = useState(valoresIniciales);
  const [registros, setRegistros] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [dialogoAbierto, setDialogoAbierto] = useState(false);
  const [tipoOperacionSeleccionada, setTipoOperacionSeleccionada] = useState('');
  const [cuentasCatalogo, setCuentasCatalogo] = useState([]);
  const [cargandoCatalogo, setCargandoCatalogo] = useState(false);

  const puedeOperar = Boolean(selectedCompany?.db_empresa);

  const cardShellSx = {
    borderRadius: 2.5,
    border: `1px solid ${FP.color.border}`,
    bgcolor: FP.color.cardBg,
    boxShadow: FP.shadow.soft,
    overflow: 'hidden',
  };

  const filterHeaderSx = {
    px: { xs: 2, md: 3 },
    py: 1.6,
    borderBottom: `1px solid ${FP.color.border}`,
    bgcolor: '#FAFBFE',
    display: 'flex',
    alignItems: 'center',
    gap: 1.2,
  };

  const cellHeadSx = {
    fontWeight: 700,
    fontSize: '0.75rem',
    color: FP.color.textSecondary,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    display: 'block',
    mb: 0.5,
  };

  const btnPrimarySx = {
    height: 38,
    bgcolor: FP.color.navy,
    color: '#FFFFFF',
    borderRadius: 1.4,
    textTransform: 'none',
    fontWeight: 700,
    boxShadow: 'none',
    '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
    '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
  };

  const btnOutlinedSx = {
    height: 38,
    borderRadius: 1.4,
    textTransform: 'none',
    fontWeight: 700,
    borderColor: FP.color.border,
    color: FP.color.navy,
    '&:hover': { borderColor: FP.color.navy, bgcolor: FP.color.surfaceAlt },
  };

  const camposRequeridosCompletos = useMemo(
    () =>
      formulario.tipoDocumento &&
      formulario.codigoRegla &&
      formulario.tipoOperacion &&
      formulario.aplicaA,
    [formulario]
  );

  const registrosAgrupados = useMemo(() => {
    const agrupado = {};
    TIPOS_OPERACION_MOTOR.forEach((op) => {
      agrupado[op] = [];
    });
    registros.forEach((item) => {
      const tipo = item.tipo_documento || item.tipoDocumento;
      if (!tipo) return;
      if (!agrupado[tipo]) {
        agrupado[tipo] = [];
      }
      agrupado[tipo].push(item);
    });
    return agrupado;
  }, [registros]);

  const registrosTipoSeleccionado = useMemo(() => {
    if (!tipoOperacionSeleccionada) return [];
    return registrosAgrupados[tipoOperacionSeleccionada] || [];
  }, [registrosAgrupados, tipoOperacionSeleccionada]);

  /** Códigos de regla permitidos según tiposDocumentos.json para el tipo de operación del motor. */
  const opcionesCodigoRegla = useMemo(() => {
    const clave = String(formulario.tipoDocumento || '').trim();
    if (!clave) return [];
    return tipo_regla1(clave);
  }, [formulario.tipoDocumento]);

  const reglasOrigenFormaPago = useMemo(
    () => origenFormaPagoReglas(formulario.tipoDocumento),
    [formulario.tipoDocumento]
  );

  const reglaUsaFormaPago = codigoUsaOrigenFormaPago(
    formulario.tipoDocumento,
    formulario.codigoRegla
  );

  const reglasOrigenTipoSeleccionado = useMemo(
    () => origenFormaPagoReglas(tipoOperacionSeleccionada),
    [tipoOperacionSeleccionada]
  );

  /** Reglas ya configuradas para la categoría (tipo_documento) del formulario. */
  const codigosReglaYaUsados = useMemo(() => {
    const tipo = String(formulario.tipoDocumento || '').trim();
    if (!tipo) return new Set();
    return new Set(
      (registros || [])
        .filter((r) => String(r.tipo_documento || r.tipoDocumento || '').trim() === tipo)
        .map((r) => String(r.codigo_regla || '').trim())
        .filter(Boolean)
    );
  }, [registros, formulario.tipoDocumento]);

  const cuentaCatalogoSeleccionada = useMemo(() => {
    const c = (formulario.cuentaContable || '').trim();
    if (!c) return null;
    return cuentasCatalogo.find((x) => String(x.cuenta ?? '').trim() === c) || null;
  }, [formulario.cuentaContable, cuentasCatalogo]);

  const cargarCuentasCatalogo = useCallback(async () => {
    if (!selectedCompany?.db_empresa) {
      setCuentasCatalogo([]);
      return;
    }
    setCargandoCatalogo(true);
    try {
      const res = await fetch(`${servidor}/contabilidadbackend_listarcatalogoactualizable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          busqueda: '',
          orden: 'cuenta',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.Error || 'No se pudo cargar el catálogo de cuentas');
      }
      setCuentasCatalogo(data.cuentas || []);
    } catch (e) {
      showError(e.message || 'Error al cargar el catálogo');
      setCuentasCatalogo([]);
    } finally {
      setCargandoCatalogo(false);
    }
  }, [selectedCompany, servidor, usuarioLog, claveLog, showError]);

  useEffect(() => {
    if (puedeLeer && selectedCompany?.db_empresa) {
      cargarCuentasCatalogo();
    }
  }, [puedeLeer, selectedCompany?.db_empresa, cargarCuentasCatalogo]);

  const actualizarCampo = (campo, valor) => {
    setFormulario((previo) => {
      const siguiente = { ...previo, [campo]: valor };
      if (campo === 'codigoRegla') {
        const usaFp = codigoUsaOrigenFormaPago(siguiente.tipoDocumento, valor);
        siguiente.aplicaA = usaFp ? 'json' : 'cuenta';
        if (usaFp) siguiente.cuentaContable = '';
      }
      return siguiente;
    });
  };

  const limpiarFormulario = (tipoDocumento = '') => {
    setFormulario({ ...valoresIniciales, tipoDocumento });
  };

  const cargarConfiguraciones = useCallback(async () => {
    if (!selectedCompany?.db_empresa) {
      setRegistros([]);
      return;
    }
    setCargando(true);
    try {
      const res = await fetch(servidor + '/contabilidadbackend_listarmotorcontable', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.Error || 'No se pudo cargar la configuración');
      }
      setRegistros(data.registros || []);
    } catch (e) {
      showError(e.message || 'Error al cargar configuración contable');
      setRegistros([]);
    } finally {
      setCargando(false);
    }
  }, [selectedCompany, servidor, usuarioLog, claveLog, showError]);

  useEffect(() => {
    if (puedeLeer) {
      cargarConfiguraciones();
    }
  }, [puedeLeer, cargarConfiguraciones]);

  const abrirDialogoNuevo = (tipoOperacionMotor) => {
    if (!puedeModificar) {
      showError('No tiene permiso para guardar configuraciones');
      return;
    }
    limpiarFormulario(tipoOperacionMotor || '');
    setDialogoAbierto(true);
  };

  const cerrarDialogo = () => {
    setDialogoAbierto(false);
    limpiarFormulario();
  };

  const guardarConfiguracion = async () => {
    if (!puedeModificar) {
      showError('No tiene permiso para guardar configuraciones');
      return;
    }
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    if (!camposRequeridosCompletos) {
      showError('Debe completar todos los campos antes de guardar');
      return;
    }
    const usaFormaPago = codigoUsaOrigenFormaPago(
      formulario.tipoDocumento,
      formulario.codigoRegla
    );
    const aplicaA = usaFormaPago || formulario.aplicaA === 'json' ? 'json' : 'cuenta';
    const cuentaGuardar = aplicaA === 'json' ? '' : (formulario.cuentaContable || '').trim();
    setGuardando(true);
    try {
      const res = await fetch(servidor + '/contabilidadbackend_guardarmotorcontable', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          tipo_documento: formulario.tipoDocumento.trim(),
          codigo_regla: formulario.codigoRegla.trim(),
          tipooperacion: formulario.tipoOperacion.trim(),
          aplica_a: aplicaA,
          cuentacontable: cuentaGuardar,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.Error || 'No se pudo guardar la configuración');
      }
      showSuccess('Configuración contable guardada correctamente');
      cerrarDialogo();
      cargarConfiguraciones();
    } catch (e) {
      showError(e.message || 'Error al guardar configuración');
    } finally {
      setGuardando(false);
    }
  };

  const eliminarRegistro = async (id) => {
    if (!puedeEliminar) {
      showError('No tiene permiso para eliminar configuraciones');
      return;
    }
    try {
      const res = await fetch(servidor + '/contabilidadbackend_eliminarmotorcontable', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          id,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.Error || 'No se pudo eliminar');
      }
      showSuccess('Configuración eliminada');
      cargarConfiguraciones();
    } catch (e) {
      showError(e.message || 'Error al eliminar');
    }
  };

  if (!puedeLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Configuración de documento contable"
          subtitle={
            nombreNegocio
              ? `${nombreNegocio} · Motor contable por operación`
              : 'Motor contable por operación'
          }
        />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{ ...cardShellSx, maxWidth: 560, width: '100%', textAlign: 'center', p: { xs: 3, md: 4 } }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                color: FP.color.textPrimary,
                mb: 1,
                letterSpacing: '-0.01em',
              }}
            >
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso del programa «configuracioncuentascontables» en roles para configurar el motor contable.
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Configuración de documento contable"
        subtitle={
          nombreNegocio
            ? `${nombreNegocio} · Motor contable por operación`
            : 'Motor contable por operación'
        }
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        {!puedeOperar && (
          <Alert
            severity="info"
            sx={{
              mb: 3,
              borderRadius: 2,
              border: `1px solid ${FP.color.border}`,
              bgcolor: FP.color.cardBg,
            }}
          >
            Seleccione una empresa con base de datos para administrar configuraciones.
          </Alert>
        )}

        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SettingsIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Tipo de operación
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.92rem', lineHeight: 1.5, mb: 2 }}>
              Seleccione el tipo de operación del motor contable para ver y administrar las reglas asociadas.
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Tipo de Operacion"
                  value={tipoOperacionSeleccionada}
                  onChange={(event) => setTipoOperacionSeleccionada(event.target.value)}
                  disabled={TIPOS_OPERACION_MOTOR.length === 0}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                  helperText={
                    TIPOS_OPERACION_MOTOR.length === 0
                      ? 'Defina opciones en motorcontable (tiposDocumentos.json).'
                      : ''
                  }
                >
                  <MenuItem value="">
                    <em>Seleccione un tipo</em>
                  </MenuItem>
                  {TIPOS_OPERACION_MOTOR.map((tipo) => (
                    <MenuItem key={tipo} value={tipo}>
                      {tipo}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    onClick={cargarConfiguraciones}
                    disabled={!puedeOperar || cargando}
                    sx={btnOutlinedSx}
                  >
                    Actualizar
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => abrirDialogoNuevo(tipoOperacionSeleccionada)}
                    disabled={
                      !puedeOperar || !puedeModificar || !usuarioLog || !tipoOperacionSeleccionada
                    }
                    sx={btnPrimarySx}
                  >
                    Agregar regla
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {cargando ? (
          <Card elevation={0} sx={cardShellSx}>
            <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: FP.color.navy }} />
            </CardContent>
          </Card>
        ) : !tipoOperacionSeleccionada ? (
          <Card elevation={0} sx={cardShellSx}>
            <CardContent>
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <SettingsIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  Seleccione un tipo de operación
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }} textAlign="center" px={2}>
                  Para ver y agregar reglas del motor contable.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <Card elevation={0} sx={cardShellSx}>
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                py: 1.6,
                borderBottom: `1px solid ${FP.color.border}`,
                bgcolor: '#FAFBFE',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
                  Reglas de {tipoOperacionSeleccionada}
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.8rem' }}>
                  {registrosTipoSeleccionado.length} regla(s) · puede agregar varias bajo la misma
                  categoría
                  {reglasOrigenTipoSeleccionado.length > 0
                    ? ` · ${reglasOrigenTipoSeleccionado.join(', ')} toma la cuenta de formas de pago`
                    : ''}
                </Typography>
              </Box>
              <Tooltip title="Agregar otra regla a esta categoría">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => abrirDialogoNuevo(tipoOperacionSeleccionada)}
                    disabled={!puedeModificar || !puedeOperar || !usuarioLog}
                    sx={{
                      color: FP.color.accentBlue,
                      '&:hover': { bgcolor: FP.color.accentBlueSoft },
                      '&.Mui-disabled': { color: FP.color.textMuted },
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, overflow: 'auto' }}
              >
                <Table size="small" sx={{ minWidth: 760 }}>
                  <TableBody>
                    <TableRow
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        '& td': {
                          border: 'none',
                          borderBottom: `2px solid ${FP.color.border}`,
                          bgcolor: FP.color.surfaceAlt,
                        },
                      }}
                    >
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Regla
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Débito / Crédito
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Aplica a
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Cuenta
                        </Typography>
                      </TableCell>
                      <TableCell align="right" className="no-print">
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                          Acción
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {registrosTipoSeleccionado.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 6, color: FP.color.textSecondary }}>
                          Sin configuraciones para este tipo.
                        </TableCell>
                      </TableRow>
                    ) : (
                      registrosTipoSeleccionado.map((registro) => (
                        <TableRow key={registro.id} hover>
                          <TableCell>{etiquetaCodigoRegla(registro.codigo_regla)}</TableCell>
                          <TableCell>{etiquetaDebitoCredito(registro.tipooperacion)}</TableCell>
                          <TableCell>
                            {etiquetaAplicaARegla(registro, tipoOperacionSeleccionada)}
                          </TableCell>
                          <TableCell>
                            {etiquetaCuentasRegla(
                              registro,
                              cuentasCatalogo,
                              tipoOperacionSeleccionada
                            )}
                          </TableCell>
                          <TableCell align="right" className="no-print">
                            <IconButton
                              size="small"
                              onClick={() => eliminarRegistro(registro.id)}
                              disabled={!puedeEliminar || !puedeOperar || !usuarioLog}
                              sx={{
                                color: FP.color.danger,
                                '&:hover': { bgcolor: FP.color.dangerSoft },
                                '&.Mui-disabled': { color: FP.color.textMuted },
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}
      </Box>

      <Dialog
        open={dialogoAbierto}
        onClose={cerrarDialogo}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.6,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.2,
              bgcolor: FP.color.accentBlueSoft,
              color: FP.color.accentBlue,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <SettingsIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
            Nuevo ítem de configuración contable
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            select
            required
            fullWidth
            size="small"
            label="Código de regla"
            value={formulario.codigoRegla}
            onChange={(event) => actualizarCampo('codigoRegla', event.target.value)}
            disabled={opcionesCodigoRegla.length === 0 || !puedeModificar}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 1, ...fpInputSx }}
            helperText={
              opcionesCodigoRegla.length === 0
                ? 'No hay reglas definidas en tiposDocumentos.json para este tipo de operación.'
                : 'Puede configurar varias reglas distintas (ej. ingreso, itbis, descuentos) en la misma categoría.'
            }
          >
            <MenuItem value="">
              <em>Seleccione una regla</em>
            </MenuItem>
            {opcionesCodigoRegla.map((codigo) => {
              const usada = codigosReglaYaUsados.has(String(codigo).trim());
              const deFormaPago = codigoUsaOrigenFormaPago(formulario.tipoDocumento, codigo);
              return (
                <MenuItem key={codigo} value={codigo} disabled={usada}>
                  {etiquetaCodigoRegla(codigo)}
                  {deFormaPago ? ' (forma de pago)' : ''}
                  {usada ? ' (ya configurada)' : ''}
                </MenuItem>
              );
            })}
          </TextField>
          <TextField
            select
            required
            fullWidth
            size="small"
            label="Tipo de operacion"
            value={formulario.tipoOperacion}
            onChange={(event) => actualizarCampo('tipoOperacion', event.target.value)}
            disabled={!puedeModificar}
            InputLabelProps={{ shrink: true }}
            sx={fpInputSx}
          >
            {LADO_DEBITO_CREDITO.map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          {reglaUsaFormaPago ? (
            <Alert
              severity="info"
              sx={{
                borderRadius: 2,
                border: `1px solid ${FP.color.border}`,
                bgcolor: FP.color.cardBg,
              }}
            >
              Esta regla ({formulario.codigoRegla}) está en <strong>origenformapago</strong>. La
              cuenta se toma de la forma de pago al procesar; no se configura aquí.
              {reglasOrigenFormaPago.length > 1
                ? ` Reglas con origen forma de pago: ${reglasOrigenFormaPago.join(', ')}.`
                : ''}
            </Alert>
          ) : (
            <TextField
              select
              required
              fullWidth
              size="small"
              label="Aplica a"
              value={formulario.aplicaA}
              onChange={(event) => {
                const v = event.target.value === 'json' ? 'json' : 'cuenta';
                setFormulario((previo) => ({ ...previo, aplicaA: v, cuentaContable: '' }));
              }}
              disabled={!puedeModificar}
              InputLabelProps={{ shrink: true }}
              sx={fpInputSx}
              helperText="Una cuenta fija, o el motor recibe un JSON con las cuentas al procesar el documento."
            >
              {APLICA_A_OPCIONES.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          )}
          {reglaUsaFormaPago ? null : formulario.aplicaA === 'json' ? (
            <Alert
              severity="info"
              sx={{
                borderRadius: 2,
                border: `1px solid ${FP.color.border}`,
                bgcolor: FP.color.cardBg,
              }}
            >
              No se pide cuenta aquí. Al procesar el documento el motor recibirá un JSON con el
              conjunto de cuentas.
            </Alert>
          ) : (
            <Autocomplete
              options={cuentasCatalogo}
              loading={cargandoCatalogo}
              value={cuentaCatalogoSeleccionada}
              onChange={(_, nueva) => {
                actualizarCampo(
                  'cuentaContable',
                  nueva && nueva.cuenta != null ? String(nueva.cuenta).trim() : ''
                );
              }}
              getOptionLabel={labelOpcionCatalogo}
              isOptionEqualToValue={(a, b) =>
                String(a?.cuenta ?? '').trim() === String(b?.cuenta ?? '').trim()
              }
              disabled={!puedeModificar}
              onOpen={() => {
                if (!cargandoCatalogo && cuentasCatalogo.length === 0 && selectedCompany?.db_empresa) {
                  cargarCuentasCatalogo();
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  label="Cuenta contable (opcional)"
                  placeholder="Buscar en catálogo (ej. 1-Activos)"
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                  helperText="Puede dejarla vacía y asignarla más adelante."
                />
              )}
            />
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.5,
            borderTop: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            gap: 1,
          }}
        >
          <Button
            onClick={cerrarDialogo}
            disabled={guardando}
            startIcon={<CloseIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: FP.color.textSecondary,
              borderRadius: 1.4,
              '&:hover': { bgcolor: FP.color.surfaceAlt },
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={guardarConfiguracion}
            disabled={
              guardando ||
              !camposRequeridosCompletos ||
              !puedeOperar ||
              !puedeModificar ||
              !usuarioLog
            }
            sx={btnPrimarySx}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConfiguracionDocumentoContable;
